/**
 * Role-Based Permission System
 * Defines which actions are allowed for each user role
 */

import { UserRole } from './auth';

export enum AuthAction {
  // Task actions
  READ_OWN_CHORES = 'READ_OWN_CHORES',
  READ_SHARED_CHORES = 'READ_SHARED_CHORES',
  CREATE_CHORE = 'CREATE_CHORE',
  EDIT_OWN_CHORE = 'EDIT_OWN_CHORE',
  EDIT_OTHER_CHORE = 'EDIT_OTHER_CHORE',
  DELETE_OWN_CHORE = 'DELETE_OWN_CHORE',
  DELETE_OTHER_CHORE = 'DELETE_OTHER_CHORE',

  // User management actions
  VIEW_USERS = 'VIEW_USERS',
  MANAGE_USERS = 'MANAGE_USERS',
  CHANGE_USER_ROLE = 'CHANGE_USER_ROLE',

  // Admin actions
  ACCESS_ADMIN_PANEL = 'ACCESS_ADMIN_PANEL',
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
}

// Define permissions for each role
const rolePermissions: Record<UserRole, Set<AuthAction>> = {
  [UserRole.Admin]: new Set([
    AuthAction.READ_OWN_CHORES,
    AuthAction.READ_SHARED_CHORES,
    AuthAction.CREATE_CHORE,
    AuthAction.EDIT_OWN_CHORE,
    AuthAction.EDIT_OTHER_CHORE,
    AuthAction.DELETE_OWN_CHORE,
    AuthAction.DELETE_OTHER_CHORE,
    AuthAction.VIEW_USERS,
    AuthAction.MANAGE_USERS,
    AuthAction.CHANGE_USER_ROLE,
    AuthAction.ACCESS_ADMIN_PANEL,
    AuthAction.VIEW_ANALYTICS,
  ]),
  [UserRole.User]: new Set([
    AuthAction.READ_OWN_CHORES,
    AuthAction.READ_SHARED_CHORES,
    AuthAction.CREATE_CHORE,
    AuthAction.EDIT_OWN_CHORE,
    AuthAction.DELETE_OWN_CHORE,
  ]),
  [UserRole.Viewer]: new Set([
    AuthAction.READ_OWN_CHORES,
    AuthAction.READ_SHARED_CHORES,
  ]),
};

/**
 * Check if a user has permission for an action.
 * Accepts a single role, an array of roles, or null/undefined.
 * When multiple roles are provided, the user is granted the union of all their roles' permissions.
 */
export function hasPermission(
  role: UserRole | UserRole[] | null | undefined,
  action: AuthAction
): boolean {
  if (!role) return false;
  const roles = Array.isArray(role) ? role : [role];
  return roles.some((r) => rolePermissions[r]?.has(action) ?? false);
}

/**
 * Returns true if the user has at least one of the given permissions.
 */
export function hasAnyPermission(
  role: UserRole | UserRole[] | null | undefined,
  actions: AuthAction[]
): boolean {
  return actions.some((action) => hasPermission(role, action));
}

/**
 * Returns true if the user has all of the given permissions.
 */
export function hasAllPermissions(
  role: UserRole | UserRole[] | null | undefined,
  actions: AuthAction[]
): boolean {
  return actions.every((action) => hasPermission(role, action));
}

/**
 * Get all permissions granted to a role (or the union across multiple roles).
 */
export function getRolePermissions(role: UserRole | UserRole[]): AuthAction[] {
  const roles = Array.isArray(role) ? role : [role];
  const merged = new Set<AuthAction>();
  for (const r of roles) {
    for (const action of rolePermissions[r] || []) {
      merged.add(action);
    }
  }
  return Array.from(merged);
}
